import { createContext, useContext, useState, useEffect } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '../config/firebase'
import {
  registerWithEmail,
  signInWithEmail,
  signInWithGoogle as googleSignIn,
  signUpWithGoogle as googleSignUp,
  signOut as authSignOut,
  getUserProfile,
  updateUserProfile as updateProfile,
  resendVerificationEmail as resendVerification
} from '../services/authService'

const AuthContext = createContext(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children, enableAuth = true }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Only initialize auth listener if auth is enabled
    if (!enableAuth) {
      setLoading(false)
      return
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user)
      
      if (user) {
        const profile = await getUserProfile(user.uid)
        setUserProfile(profile)
        // Note: Last sign-in time is available via user.metadata.lastSignInTime from Firebase Auth
      } else {
        setUserProfile(null)
      }
      
      setLoading(false)
    })

    return unsubscribe
  }, [enableAuth])

  const signUp = async (data) => {
    const result = await registerWithEmail(data)
    if (!result.success) {
      throw new Error(result.error?.message || 'Registration failed')
    }
  }

  const signIn = async (data) => {
    const result = await signInWithEmail(data)
    if (!result.success) {
      throw new Error(result.error?.message || 'Sign in failed')
    }
  }

  const signInWithGoogle = async () => {
    const result = await googleSignIn()
    if (!result.success) {
      throw new Error(result.error?.message || 'Google sign in failed')
    }
  }

  const signUpWithGoogle = async () => {
    const result = await googleSignUp()
    if (!result.success) {
      throw new Error(result.error?.message || 'Google sign up failed')
    }
  }

  const signOut = async () => {
    const result = await authSignOut()
    if (!result.success) {
      throw new Error(result.error?.message || 'Sign out failed')
    }
  }

  const updateUserProfile = async (updates) => {
    if (!currentUser) {
      throw new Error('No user signed in')
    }
    const result = await updateProfile(currentUser.uid, updates)
    if (!result.success) {
      throw new Error(result.error?.message || 'Profile update failed')
    }
    // Refresh user profile
    const updatedProfile = await getUserProfile(currentUser.uid)
    setUserProfile(updatedProfile)
  }

  const resendVerificationEmail = async () => {
    const result = await resendVerification()
    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to send verification email')
    }
  }

  const value = {
    currentUser,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signUpWithGoogle,
    signOut,
    updateUserProfile,
    resendVerificationEmail
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
